import React, { useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { useIntl } from 'react-intl';

import { useAnalyticsContext } from 'app/shared/utils';
import { AuthFormType, OnSignUpSuccess, User } from 'app/shared/typings';
import { AuthContext } from 'app/shared/context/Auth';
import { ActionButton } from 'app/shared/components/molecules';
import { AuthModal } from 'app/shared/components/organisms';
import { UPDATE_ARTIST_FOLLOWING } from 'app/fan/graphql/artistFollowings/mutations';
import { GetArtistFollowingLazy } from 'app/fan/graphql/artistFollowings/queryHooks';
import { ReactComponent as LoveIt } from 'icons/streamline-regular/social-medias-rewards-rating/love-it/love-it.svg';

interface FollowArtistButtonProps {
  artistId: number;
  // If true, this will attempt to automatically follow the artist
  autoFollow?: boolean;
}

export const FollowArtistButton: React.FC<
  React.PropsWithChildren<FollowArtistButtonProps>
> = ({ artistId, autoFollow }) => {
  const { loggedIn, user, signIn } = useContext(AuthContext);
  const { formatMessage } = useIntl();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [currentTabSelected, setCurrentTabSelected] =
    useState<AuthFormType>('signUp');
  const [isFollowing, setIsFollowing] = useState<boolean>(false);
  const { trackAnalyticsEvent } = useAnalyticsContext();

  const [getFollowing, { data: followingData, loading: followingLoading }] =
    GetArtistFollowingLazy({
      artistId,
      userId: user?.id,
    });

  const [
    updateFollowing,
    { data: updateFollowingData, loading: updateFollowingLoading },
  ] = useMutation(UPDATE_ARTIST_FOLLOWING);

  function updateAndTrackFollowing(user: User) {
    updateFollowing({
      variables: {
        artistId,
        userId: user?.id,
        following: !isFollowing,
      },
    });
    trackAnalyticsEvent('Follow Artist', {
      artistId,
      userId: user?.id,
      following: !isFollowing,
    });
  }

  const updateFollowingOrOpenModal = () => {
    if (user) {
      updateAndTrackFollowing(user);
    } else {
      setIsAuthModalOpen(true);
    }
  };

  // Get following on page load for signed-in users
  useEffect(() => {
    if (user && artistId && !followingData) {
      getFollowing();
    }
  }, [user, artistId, followingData]);

  useEffect(() => {
    if (updateFollowingData) {
      setIsFollowing(
        updateFollowingData.updateArtistFollowing.artistFollowing.following
      );
    }
  }, [updateFollowingData]);

  useEffect(() => {
    if (followingData) {
      const notAlreadyFollowing =
        followingData.artistFollowing == null ||
        !followingData.artistFollowing?.following;
      if (notAlreadyFollowing && autoFollow) {
        updateFollowingOrOpenModal();
      } else {
        setIsFollowing(followingData.artistFollowing?.following);
      }
    } else if (!loggedIn && autoFollow) {
      updateFollowingOrOpenModal();
    }
  }, [followingData, autoFollow, loggedIn]);

  const handleSuccessfulSignIn: OnSignUpSuccess = (signUpSucessParams) => {
    const newUser = signUpSucessParams?.user;

    if (!newUser) {
      return;
    }

    signIn();
    updateAndTrackFollowing(newUser);
  };

  return (
    <>
      <>
        <ActionButton
          onClick={updateFollowingOrOpenModal}
          variant={isFollowing ? 'tertiary' : 'primary'}
          isLoading={followingLoading || updateFollowingLoading}
          data-qaid="artist-follow-button"
          leadingIcon={LoveIt}
        >
          {formatMessage({
            id: isFollowing
              ? 'artistFollowButton.following'
              : 'artistFollowButton.follow',
          })}
        </ActionButton>
        <AuthModal
          data-qaid="follow-artist-auth-modal"
          onSuccess={handleSuccessfulSignIn}
          parentContainer="Follow Artist Button"
          signUpHeader={formatMessage({
            id: 'artistFollowButton.signUpHeader',
          })}
          setIsOpen={setIsAuthModalOpen}
          isOpen={isAuthModalOpen}
          currentTabSelected={currentTabSelected}
          setCurrentTabSelected={setCurrentTabSelected}
        />
      </>
    </>
  );
};
